import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, Users, Zap, Award, Globe, ArrowRight, ChevronDown } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';
import { REGISTER_URL } from '../config/app';

export default function Programs() {
  const { t } = useLanguage();
  useSEO({
    title: 'Programs',
    description:
      'Discover GRAWEO programs: professional training, mentorship, networking, grants, and business accelerator support for sustainable recovery.',
  });
  const [expandedProgram, setExpandedProgram] = useState(0);

  const programs = [
    {
      icon: BookOpen,
      title: t('program_training_title'),
      shortDesc: t('program_training_short'),
      fullDesc: t('program_training_full'),
      benefits: [
        t('program_training_benefit_1'),
        t('program_training_benefit_2'),
        t('program_training_benefit_3'),
        t('program_training_benefit_4'),
      ],
      duration: t('program_training_duration'),
      cost: t('program_training_cost'),
    },
    {
      icon: Users,
      title: t('program_mentorship_title'),
      shortDesc: t('program_mentorship_short'),
      fullDesc: t('program_mentorship_full'),
      benefits: [
        t('program_mentorship_benefit_1'),
        t('program_mentorship_benefit_2'),
        t('program_mentorship_benefit_3'),
        t('program_mentorship_benefit_4'),
      ],
      duration: t('program_mentorship_duration'),
      cost: t('program_mentorship_cost'),
    },
    {
      icon: Globe,
      title: t('program_networking_title'),
      shortDesc: t('program_networking_short'),
      fullDesc: t('program_networking_full'),
      benefits: [
        t('program_networking_benefit_1'),
        t('program_networking_benefit_2'),
        t('program_networking_benefit_3'),
        t('program_networking_benefit_4'),
      ],
      duration: t('program_networking_duration'),
      cost: t('program_networking_cost'),
    },
    {
      icon: Award,
      title: t('program_grants_title'),
      shortDesc: t('program_grants_short'),
      fullDesc: t('program_grants_full'),
      benefits: [
        t('program_grants_benefit_1'),
        t('program_grants_benefit_2'),
        t('program_grants_benefit_3'),
        t('program_grants_benefit_4'),
      ],
      duration: t('program_grants_duration'),
      cost: t('program_grants_cost'),
    },
    {
      icon: Zap,
      title: t('program_accelerator_title'),
      shortDesc: t('program_accelerator_short'),
      fullDesc: t('program_accelerator_full'),
      benefits: [
        t('program_accelerator_benefit_1'),
        t('program_accelerator_benefit_2'),
        t('program_accelerator_benefit_3'),
        t('program_accelerator_benefit_4'),
      ],
      duration: t('program_accelerator_duration'),
      cost: t('program_accelerator_cost'),
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
              {t('programs_title')}
            </h1>
            <p className="text-xl text-gray-100 leading-relaxed">
              {t('programs_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('programs_available')}
          </h2>

          <div className="space-y-6">
            {programs.map((program, index) => {
              const Icon = program.icon;
              const isExpanded = expandedProgram === index;

              return (
                <Card
                  key={index}
                  className="overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <button
                    onClick={() =>
                      setExpandedProgram(isExpanded ? -1 : index)
                    }
                    className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-14 w-14 rounded-lg bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white">
                        <Icon className="w-7 h-7" />
                      </div>
                    </div>

                    <div className="flex-1 text-left">
                      <h3 className="text-xl font-bold font-manrope text-gray-900 mb-1">
                        {program.title}
                      </h3>
                      <p className="text-gray-600">
                        {program.shortDesc}
                      </p>
                    </div>

                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50 p-6">
                      <div className="space-y-6">
                        {/* Full Description */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('program_about')}
                          </h4>
                          <p className="text-gray-700 leading-relaxed">
                            {program.fullDesc}
                          </p>
                        </div>

                        {/* Benefits */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">
                            {t('program_benefits')}
                          </h4>
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {program.benefits.map((benefit, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-3"
                              >
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-graweo-olive/10 flex items-center justify-center mt-0.5">
                                  <span className="text-graweo-olive font-bold text-sm">
                                    ✓
                                  </span>
                                </div>
                                <span className="text-gray-700">
                                  {benefit}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Details */}
                        <div className="bg-white rounded-lg p-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">
                              {t('program_duration')}:
                            </span>
                            <span className="text-gray-900 font-semibold">
                              {program.duration}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-t pt-3">
                            <span className="text-gray-600 font-medium">
                              {t('program_cost')}:
                            </span>
                            <span className="text-gray-900 font-semibold">
                              {program.cost}
                            </span>
                          </div>
                        </div>

                        {/* Apply Button */}
                        <a href={REGISTER_URL}>
                          <Button
                            variant="primary"
                            className="w-full"
                          >
                            {t('program_apply')}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('programs_why_choose')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-graweo-terra/10 rounded-full flex items-center justify-center">
                  <Award className="w-8 h-8 text-graweo-terra" />
                </div>
              </div>
              <h3 className="text-xl font-bold font-manrope text-gray-900 mb-3">
                {t('programs_feature_1_title')}
              </h3>
              <p className="text-gray-600">
                {t('programs_feature_1_desc')}
              </p>
            </Card>

            <Card className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-graweo-olive/10 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-graweo-olive" />
                </div>
              </div>
              <h3 className="text-xl font-bold font-manrope text-gray-900 mb-3">
                {t('programs_feature_2_title')}
              </h3>
              <p className="text-gray-600">
                {t('programs_feature_2_desc')}
              </p>
            </Card>

            <Card className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-graweo-chocolate/10 rounded-full flex items-center justify-center">
                  <Globe className="w-8 h-8 text-graweo-chocolate" />
                </div>
              </div>
              <h3 className="text-xl font-bold font-manrope text-gray-900 mb-3">
                {t('programs_feature_3_title')}
              </h3>
              <p className="text-gray-600">
                {t('programs_feature_3_desc')}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-8 text-center">
            {t('programs_how_apply')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4].map((step) => (
              <Card key={step} className="p-6 flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-graweo-terra to-graweo-terra/80 text-white font-bold">
                    {step}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {t(`programs_apply_step_${step}_title`)}
                  </h4>
                  <p className="text-gray-600">
                    {t(`programs_apply_step_${step}_desc`)}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            {t('programs_cta_title')}
          </h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            {t('programs_cta_text')}
          </p>
          <a href={REGISTER_URL}>
            <Button variant="secondary" size="lg">
              {t('programs_cta_button')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
